import { useEffect, useState } from "react";
import { Warning, X } from "@phosphor-icons/react";

const LS_KEY = "etabuild_legal_ack_v1";

/**
 * Disclaimer banner shown inline above any calculation/quote module.
 * Always visible — required for legal compliance.
 */
export function DisclaimerBanner() {
  return (
    <div
      data-testid="disclaimer-banner"
      className="mb-4 bg-[#FFF7CC]/5 border-l-4 border-[#FFD600] rounded-md p-3 flex gap-3"
    >
      <Warning size={20} weight="fill" className="text-[#FFD600] shrink-0 mt-0.5" />
      <p className="text-xs text-[#D4D4D4] leading-snug">
        <b className="text-[#FFD600] uppercase">Avvertenza tecnica.</b>{" "}
        I calcoli generati sono <b>stime indicative</b>. È obbligatorio consultare un
        tecnico abilitato (architetto, ingegnere o geometra) per la convalida del progetto
        e della sicurezza in cantiere (D.Lgs. 81/2008).
      </p>
    </div>
  );
}

/**
 * Full-screen modal shown the first time the user opens a calculation module.
 * Stored once in localStorage; user must press "Ho capito" to dismiss.
 */
export function DisclaimerModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem(LS_KEY)) setOpen(true);
  }, []);

  const accept = () => {
    localStorage.setItem(LS_KEY, new Date().toISOString());
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="disclaimer-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-[#1A1A1A] border-2 border-[#FFD600] rounded-lg max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <Warning size={32} weight="fill" className="text-[#FFD600] shrink-0" />
          <div>
            <h2 className="font-heading text-xl font-black uppercase leading-tight">
              Scarico di responsabilità
            </h2>
            <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mt-1">
              Modulo di calcolo · ETA Build
            </p>
          </div>
        </div>

        <div className="text-sm text-[#D4D4D4] leading-relaxed space-y-3">
          <p>
            I calcoli, le quantità e i prezzi generati da ETA Build sono <b>stime indicative</b>{" "}
            prodotte da algoritmi automatici e da modelli di intelligenza artificiale.
          </p>
          <p>
            È <b className="text-[#FFD600]">obbligatorio consultare un tecnico abilitato</b>{" "}
            (architetto, ingegnere o geometra iscritto al rispettivo Ordine professionale)
            per la convalida del progetto, il dimensionamento definitivo e la verifica della
            sicurezza in cantiere ai sensi del D.Lgs. 81/2008.
          </p>
          <p className="text-xs text-[#A3A3A3]">
            Cliccando su "Ho capito" dichiari di aver letto e compreso questa avvertenza.
            Le stime fornite non costituiscono progetto esecutivo né relazione tecnica
            asseverata.
          </p>
        </div>

        <button
          data-testid="disclaimer-accept"
          onClick={accept}
          className="mt-6 w-full bg-[#FFD600] text-[#0A0A0A] h-14 rounded-md font-black uppercase text-base hover:bg-[#E6C200] transition flex items-center justify-center gap-2"
        >
          <X size={20} weight="bold" /> Ho capito, prosegui
        </button>
      </div>
    </div>
  );
}
